/* eslint-disable no-unsafe-optional-chaining */
import { faChevronDown, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { chain, filter, keys, mapValues, pickBy, sortBy } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FormSpy, useForm } from "react-final-form";
import { Badge, Button, Table } from "reactstrap";

import TreeSelect, { SHOW_PARENT } from "rc-tree-select";
import { Card, CardBody, FormGroup, Label } from "resbutler-ui/components/WizElements/Elements";
import { validateMenuHaveProduct } from "resbutler-utils/utils/menuUtils";
import { Menus } from "../../resbutler-utils/types/Menu";
import { Product, ProductSizes, ProductStock, StockScope } from "../../resbutler-utils/types/product";
import { OverrideStockForm } from "./OverrideStockForm";

type StockItemFormProps = {
  product: Product;
  menus: Menus;
  restaurantId: string;
  productSizes: ProductSizes;
  date: number;
};

const StockItemForm: React.FC<StockItemFormProps> = ({ product, menus, restaurantId, productSizes, date }) => {
  const [collapse, setCollapse] = useState<Record<string, boolean>>({});
  const [menuTreeData, setMenuTreeData] = useState<any>(null);
  const form = useForm();
  const stockTypeKey = "products";

  const toggleCollapse = useCallback((id: string) => {
    setCollapse((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  const enabledProductSizes = useMemo(() => {
    if (!product.sizes) return [];
    return chain(product.sizes)
      .pickBy((enabled, sizeId) => enabled && productSizes[sizeId])
      .map((_, sizeId) => sizeId)
      .value();
  }, [product.sizes, productSizes]);

  useEffect(() => {
    const headingTree: any[] = [];
    Object.entries(menus).forEach(([menuId, menu]: any) => {
      const errror = validateMenuHaveProduct(product.id, "", menu, {});
      if (!errror) {
        headingTree.push({
          key: menuId,
          title: menu.name,
          value: menuId,
          disabled: false,
        });
      }
    });
    setMenuTreeData(headingTree);
  }, [menus, product.id]);

  return (
    <FormSpy subscription={{ values: true }}>
      {({ values }) => {
        const productStockValues: ProductStock = values?.[stockTypeKey]?.[product.id];
        return (
          <>
            <tr>
              <td>
                <Button type="button" className="me-2" onClick={() => toggleCollapse(product.id)}>
                  <FontAwesomeIcon icon={collapse[product.id] ? faChevronDown : faChevronRight} />
                </Button>

                {product.name}

                <Badge color="info" className="ms-1">
                  {filter(productStockValues?.sizes, (v) => v.enabled).length > 0 ? "override" : ""}
                </Badge>
              </td>
            </tr>

            {collapse[product.id] && (
              <tr>
                <td>
                  <Card>
                    <CardBody>
                      <OverrideStockForm date={date} restaurantId={restaurantId} enabledProductSizes={enabledProductSizes} productSizes={productSizes} product={product} stockTypeKey={stockTypeKey} />

                      {filter(productStockValues?.sizes, (v) => v.enabled).length > 0 && (
                        <>
                          <div className="checkbox c-checkbox mt-4 mb-2">
                            <label>
                              <input
                                type="checkbox"
                                checked={productStockValues?.stockScope === StockScope.All}
                                onChange={(e) => {
                                  form.change(`${stockTypeKey}.${product.id}.stockScope`, e.target.checked ? StockScope.All : StockScope.Menus);
                                }}
                              />
                              Applies to all menu
                            </label>
                          </div>
                          {productStockValues?.stockScope !== StockScope.All && (
                            <div>
                              <FormGroup>
                                <Label>Menus</Label>
                                <TreeSelect
                                  className="form-control tree-select"
                                  style={{ width: "100%" }}
                                  choiceTransitionName="rc-tree-select-selection__choice-zoom"
                                  dropdownStyle={{ height: 100, overflow: "auto" }}
                                  dropdownPopupAlign={{ overflow: { adjustY: 0, adjustX: 0 }, offset: [0, 2] }}
                                  treeLine
                                  value={keys(pickBy(productStockValues?.enabledMenus, (v) => v.enabled))}
                                  placeholder={"Select menu"}
                                  showIcon={false}
                                  maxTagTextLength={25}
                                  autoClearSearchValue
                                  treeData={menuTreeData}
                                  treeCheckable
                                  showCheckedStrategy={SHOW_PARENT}
                                  maxTagCount={20}
                                  onChange={(ids) => {
                                    const existingMenus = { ...productStockValues?.enabledMenus };
                                    const enabledMenus = mapValues(existingMenus, (data, menuId) => ({
                                      ...data,
                                      enabled: ids.includes(menuId),
                                    }));

                                    ids.forEach((menuId) => {
                                      if (!enabledMenus[menuId]) {
                                        enabledMenus[menuId] = { enabled: true };
                                      }
                                    });

                                    form.change(`${stockTypeKey}.${product.id}.enabledMenus`, enabledMenus);
                                  }}
                                  maxTagPlaceholder={(valueList) => `+${valueList.length}`}
                                />
                              </FormGroup>
                            </div>
                          )}
                        </>
                      )}
                    </CardBody>
                  </Card>{" "}
                </td>
              </tr>
            )}
          </>
        );
      }}
    </FormSpy>
  );
};

interface StockProductFormProps {
  menus: Menus;
  restaurantId: string;
  productSizes: ProductSizes;
  filteredProducts: Product[];
  date: number;
}

const StockProductForm: React.FC<StockProductFormProps> = ({ menus, restaurantId, productSizes, filteredProducts, date }) => {
  const sortedProducts = useMemo(() => sortBy(filteredProducts, (p) => p.name), [filteredProducts]);

  return (
    <Table bordered className="sc-table">
      <tbody>
        {sortedProducts.map((product) => (
          <StockItemForm key={`menu-item-stock-override-products-${product.id}`} product={product} menus={menus} restaurantId={restaurantId} productSizes={productSizes} date={date} />
        ))}
      </tbody>
    </Table>
  );
};

export default StockProductForm;
